import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/EmptyState';
import { StatusBadge } from '@/components/StatusBadge';
import { LevelPill } from '@/components/apps/LevelPill';
import { api } from '@/lib/api';
import { formatAgo } from '@/lib/ago';
import { formatDuration } from '@/lib/format';
import { useAdaptivePolledData } from '@/lib/hooks/useAdaptivePolledData';
import { usePolledData } from '@/lib/hooks/usePolledData';
import type { App } from '@/types/app';
import type { Deployment } from '@/types/deployment';

const POLL_MS = 3000;
const scrollClass =
  'max-h-[60vh] overflow-auto rounded-md border bg-muted/40 outline-none focus-visible:ring-2 focus-visible:ring-ring';

const isActive = (d: Deployment | undefined) => d?.status === 'pending' || d?.status === 'in_progress';

function When({ iso }: { iso: string | null }) {
  const t = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(t)) return <>—</>;
  return (
    <>
      {formatAgo(t)} <span className="text-muted-foreground">({new Date(t).toLocaleString()})</span>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
      <dt className="w-28 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

export function DeploymentTab({ app }: { app: App }) {
  const id = app.deployment?.id;
  if (!id) return <EmptyState as="h3" title="No deployment info available" className="border-0 py-8" />;
  return <DeploymentBody key={id} id={id} />;
}

function DeploymentBody({ id }: { id: number }) {
  // Cadence follows the latest deployment status: 3s while pending/in_progress, otherwise a single fetch.
  const dep = useAdaptivePolledData(() => api.deployments.get(id), (d) => (isActive(d) ? POLL_MS : 0), 0);
  const logs = usePolledData(() => api.deployments.getLogs(id), isActive(dep.data) ? POLL_MS : undefined);

  if (dep.data === undefined) {
    if (dep.loading) {
      return (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      );
    }
    return (
      <EmptyState
        as="h3"
        title="Could not load deployment"
        description={dep.error ?? undefined}
        action={
          <Button variant="outline" size="sm" onClick={dep.refetch}>
            Retry
          </Button>
        }
      />
    );
  }

  const d = dep.data;
  const created = Date.parse(d.created_at);
  const completed = d.completed_at ? Date.parse(d.completed_at) : NaN;
  const duration = Number.isNaN(created) || Number.isNaN(completed) ? null : completed - created;
  const entries = logs.data ?? [];
  const retry = () => {
    dep.refetch();
    logs.refetch();
  };

  return (
    <div className="space-y-4">
      {(dep.error !== null || logs.error !== null) && (
        <div
          role="status"
          className="flex items-center justify-between gap-2 rounded bg-status-warning-soft px-2 py-1 text-xs text-status-warning"
        >
          <span>Showing last known data — refresh failed.</span>
          <Button variant="ghost" size="xs" onClick={retry}>
            Retry
          </Button>
        </div>
      )}
      <Card className="gap-3 p-4">
        <h2 className="text-base font-semibold">Deployment</h2>
        <dl className="space-y-1.5 text-sm">
          <Row label="ID"><span className="font-mono text-xs">{d.id}</span></Row>
          <Row label="Type"><span className="capitalize">{d.type}</span></Row>
          <Row label="Status"><StatusBadge status={d.status} /></Row>
          <Row label="Created"><When iso={d.created_at} /></Row>
          <Row label="Completed"><When iso={d.completed_at} /></Row>
          <Row label="Duration">{formatDuration(duration)}</Row>
          <Row label="Instance ID">{d.instance_id}</Row>
          <Row label="Endpoint ID">{d.endpoint_id}</Row>
        </dl>
      </Card>

      <section className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold">Deployment log</h2>
          <Button size="sm" variant="outline" onClick={logs.refetch}>
            <RefreshCw className="size-4" aria-hidden /> Refresh
          </Button>
        </div>
        {logs.data === undefined ? (
          logs.loading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <EmptyState
              as="h3"
              title="Could not load deployment log"
              description={logs.error ?? undefined}
              action={
                <Button variant="outline" size="sm" onClick={logs.refetch}>
                  Retry
                </Button>
              }
            />
          )
        ) : entries.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No log entries yet</div>
        ) : (
          <div role="region" tabIndex={0} aria-label="Deployment log" className={`${scrollClass} space-y-1 p-2`}>
            {entries.map((log) => {
              const t = Date.parse(log.timestamp);
              return (
                <div key={log.id} className="flex items-start gap-2 text-sm">
                  <span className="shrink-0 pt-0.5 font-mono text-xs text-muted-foreground">
                    {Number.isNaN(t) ? '—' : new Date(t).toLocaleString()}
                  </span>
                  <LevelPill level={log.level} />
                  <span className="min-w-0 flex-1 break-words">{log.message}</span>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-sm text-muted-foreground">Showing the latest deployment.</p>
      </section>
    </div>
  );
}
